// China EXPO News Service
// This service handles fetching and caching latest China EXPO news

class ChinaExpoNewsService {
  constructor() {
    this.cacheKey = 'chinaExpoNews';
    this.lastUpdateKey = 'chinaExpoNewsLastUpdate';
    this.cacheTimeout = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds
  }

  // Get the latest China EXPO news
  async getLatestNews() {
    try {
      // Check if we have cached data that's still fresh
      const cachedData = this.getCachedNews();
      if (cachedData && this.isCacheValid()) {
        return cachedData;
      }

      // If no cache or cache is expired, fetch new data
      const freshNews = await this.fetchFreshNews();
      this.cacheNews(freshNews);
      return freshNews;
    } catch (error) {
      console.error('Error fetching China EXPO news:', error);
      // Return fallback news if fetch fails
      return this.getFallbackNews();
    }
  }

  // Check if cached data is still valid (less than 30 days old)
  isCacheValid() {
    const lastUpdate = localStorage.getItem(this.lastUpdateKey);
    if (!lastUpdate) return false;
    
    const lastUpdateTime = parseInt(lastUpdate);
    const now = Date.now();
    return (now - lastUpdateTime) < this.cacheTimeout;
  }

  // Get cached news from localStorage
  getCachedNews() {
    try {
      const cached = localStorage.getItem(this.cacheKey);
      return cached ? JSON.parse(cached) : null;
    } catch (error) {
      console.error('Error reading cached news:', error);
      return null;
    }
  }

  // Cache news to localStorage
  cacheNews(news) {
    try {
      localStorage.setItem(this.cacheKey, JSON.stringify(news));
      localStorage.setItem(this.lastUpdateKey, Date.now().toString());
    } catch (error) {
      console.error('Error caching news:', error);
    }
  }

  // Simulate fetching fresh news (in real implementation, this would call an API)
  async fetchFreshNews() {
    // Generate fresh news with current dates and rotating content
    const currentMonth = new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    const currentYear = new Date().getFullYear();
    
    // Rotating news pool - different articles appear on each refresh
    const newsPool = [
      {
        id: `expo-${Date.now()}-1`,
        title: `China International Import Expo (CIIE) ${currentYear + 1}: Registration Now Open`,
        excerpt: `The 8th CIIE, scheduled for November 5-10, ${currentYear + 1} in Shanghai, features over 360,000 square meters of exhibition space. Professional visitor registration now available.`,
        image: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?ixlib=rb-4.0.3&auto=format&fit=crop&w=2340&q=80",
        date: this.getCurrentMonthDate(2),
        category: `CIIE ${currentYear + 1}`,
        readTime: "4 min read",
        source: "CIIE Official",
        url: "https://www.ciie.org/zbh/en/Registration/"
      },
      {
        id: `expo-${Date.now()}-2`,
        title: `Canton Fair ${currentYear}: World's Largest Trade Fair Returns to Guangzhou`,
        excerpt: `The 138th Canton Fair runs October 15 - November 4, ${currentYear} in Guangzhou, expecting over 250,000 international buyers from 214+ countries.`,
        image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&auto=format&fit=crop&w=2340&q=80",
        date: this.getCurrentMonthDate(1),
        category: "Canton Fair",
        readTime: "5 min read",
        source: "Canton Fair Official",
        url: "https://www.cantonfair.org.cn/en-US"
      },
      {
        id: `expo-${Date.now()}-3`,
        title: `${currentMonth}: China's Green Manufacturing Revolution Accelerates`,
        excerpt: "China's manufacturing sector leads global sustainability efforts with 40% renewable energy integration and carbon-neutral factory initiatives across major industrial zones.",
        image: "https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?ixlib=rb-4.0.3&auto=format&fit=crop&w=2340&q=80",
        date: this.getCurrentMonthDate(0),
        category: "Sustainability",
        readTime: "5 min read",
        source: "China Daily",
        url: "https://www.chinadaily.com.cn/"
      },
      {
        id: `expo-${Date.now()}-4`,
        title: `New Trade Policy Update ${currentMonth}: Streamlined Import-Export Procedures`,
        excerpt: "China announces simplified customs clearance processes and reduced documentation requirements for international trade partners, effective immediately.",
        image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&auto=format&fit=crop&w=2340&q=80",
        date: this.getCurrentMonthDate(3),
        category: "Trade Policy",
        readTime: "4 min read",
        source: "Ministry of Commerce",
        url: "http://english.mofcom.gov.cn/"
      },
      {
        id: `expo-${Date.now()}-5`,
        title: `${currentYear} Q4 Manufacturing Outlook: AI-Driven Production Gains Momentum`,
        excerpt: "Chinese manufacturers report 35% productivity increase through AI integration, with smart factory implementations growing 50% year-over-year.",
        image: "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?ixlib=rb-4.0.3&auto=format&fit=crop&w=2340&q=80",
        date: this.getCurrentMonthDate(1),
        category: "Technology",
        readTime: "6 min read",
        source: "Manufacturing Today",
        url: "https://www.globaltimes.cn/"
      },
      {
        id: `expo-${Date.now()}-6`,
        title: `Supply Chain Resilience: China's New Logistics Infrastructure ${currentMonth}`,
        excerpt: "Major logistics hubs in Guangzhou, Shanghai, and Xiamen expand capacity with automated warehousing and AI-powered distribution systems.",
        image: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?ixlib=rb-4.0.3&auto=format&fit=crop&w=2340&q=80",
        date: this.getCurrentMonthDate(2),
        category: "Logistics",
        readTime: "5 min read",
        source: "Supply Chain Asia",
        url: "https://www.ciie.org/zbh/en/"
      }
    ];
    
    // Return 3 random articles from the pool
    const shuffled = newsPool.sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 3);
  }

  // Generate a current month date
  getCurrentMonthDate(daysAgo) {
    const date = new Date();
    date.setDate(date.getDate() - daysAgo);
    return date.toLocaleDateString('en-US', { 
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  }

  // Fallback news in case of fetch failure
  getFallbackNews() {
    return [
      {
        id: 'fallback-1',
        title: "China EXPO News: Connecting Global Markets",
        excerpt: "Stay updated with the latest developments in China's major trade exhibitions and import-export opportunities.",
        image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&auto=format&fit=crop&w=2340&q=80",
        date: new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }),
        category: "Trade News",
        readTime: "3 min read",
        source: "Tower Technology",
        url: "https://www.ciie.org/zbh/en/",
        fullContent: `Stay informed about the latest developments in China's major trade exhibitions and import-export opportunities. Our platform provides comprehensive coverage of key industry events, helping businesses navigate the evolving landscape of China-global trade relationships.

**What We Cover:**
• Major trade fairs and exhibitions (CIIE, Canton Fair, etc.)
• Import-export regulations and updates
• Market trends and business opportunities
• Supplier verification and quality assurance insights
• Logistics and supply chain developments

Connect with our sourcing experts for personalized guidance on your China trade initiatives.`
      }
    ];
  }

  // Manually refresh news (can be called from admin or by user action)
  async forceRefresh() {
    try {
      const freshNews = await this.fetchFreshNews();
      this.cacheNews(freshNews);
      return freshNews;
    } catch (error) {
      console.error('Error force refreshing news:', error);
      throw error;
    }
  }

  // Get time until next auto-update
  getTimeUntilNextUpdate() {
    const lastUpdate = localStorage.getItem(this.lastUpdateKey);
    if (!lastUpdate) return 0;
    
    const lastUpdateTime = parseInt(lastUpdate);
    const nextUpdate = lastUpdateTime + this.cacheTimeout;
    const now = Date.now();
    
    return Math.max(0, nextUpdate - now);
  }

  // Get formatted time until next update
  getFormattedTimeUntilUpdate() {
    const timeLeft = this.getTimeUntilNextUpdate();
    const days = Math.floor(timeLeft / (24 * 60 * 60 * 1000));
    
    if (days === 0) return "Updates available now";
    if (days === 1) return "Next update in 1 day";
    return `Next update in ${days} days`;
  }
}

export default new ChinaExpoNewsService();